<template>
	<div class="container">
		<h1>Admin: User Management</h1>
		<div class="mb-3"><b>Total Users:</b> {{ total }}</div>
		<div class="mb-3">
			<input
				v-model="search"
				@input="onSearch"
				type="text"
				class="form-control"
				placeholder="Search by email..."
				style="max-width: 300px; display: inline-block; margin-right: 1rem" />
		</div>
		<!-- User Cards -->
		<div class="d-flex flex-row flex-wrap gap-3">
			<div
				v-for="user in users"
				:key="user.id"
				class="card shadow-sm mb-3"
				style="min-width: 260px; max-width: 320px; flex: 1 1 260px">
				<div class="card-body">
					<h5 class="card-title mb-2">{{ user.email }}</h5>
					<div class="mb-1"><b>ID:</b> {{ user.id }}</div>
					<div class="mb-1"><b>Role:</b> {{ user.role }}</div>
					<div class="mb-1"><b>Created:</b> {{ user.created_at }}</div>
					<div class="mb-1"><b>Item Count:</b> {{ user.item_count }}</div>
					<div class="d-flex flex-wrap gap-2 mt-2">
						<button
							class="btn btn-sm btn-outline-primary"
							@click="openSubscriptionModal(user)">
							View Subscription
						</button>
						<button
							v-if="user.role === 'user'"
							class="btn btn-sm btn-outline-success"
							@click="changeRole(user, 'admin')">
							Make Admin
						</button>
						<button
							v-else
							class="btn btn-sm btn-outline-danger"
							@click="changeRole(user, 'user')"
							:disabled="+user.id === +userStore.user.id">
							Remove Admin
						</button>
					</div>
				</div>
			</div>
		</div>
		<div class="d-flex align-items-center mt-3 gap-3">
			<!-- Page size dropdown -->
			<div>
				<label for="pageSizeSelect" class="me-2">Rows per page:</label>
				<select
					id="pageSizeSelect"
					v-model="pageSizeStr"
					@change="onPageSizeChange"
					class="form-select d-inline-block w-auto">
					<option value="10">10</option>
					<option value="20">20</option>
					<option value="50">50</option>
					<option value="100">100</option>
					<option value="ALL">ALL</option>
				</select>
			</div>
			<!-- Pagination controls -->
			<nav v-if="totalPages > 1 && pageSizeStr !== 'ALL'">
				<ul class="pagination mb-0">
					<li class="page-item" :class="{ disabled: page === 1 }">
						<button
							class="page-link"
							@click="goToPage(1)"
							:disabled="page === 1"
							title="First">
							&laquo;
						</button>
					</li>
					<li class="page-item" :class="{ disabled: page === 1 }">
						<button
							class="page-link"
							@click="prevPage"
							:disabled="page === 1"
							title="Previous">
							&lsaquo;
						</button>
					</li>
					<li
						v-for="p in pageNumbers"
						:key="p.key"
						class="page-item"
						:class="{ active: p.num === page, disabled: p.ellipsis }">
						<button
							v-if="!p.ellipsis"
							class="page-link"
							@click="goToPage(p.num)">
							{{ p.num }}
						</button>
						<span v-else class="page-link">…</span>
					</li>
					<li class="page-item" :class="{ disabled: page === totalPages }">
						<button
							class="page-link"
							@click="nextPage"
							:disabled="page === totalPages"
							title="Next">
							&rsaquo;
						</button>
					</li>
					<li class="page-item" :class="{ disabled: page === totalPages }">
						<button
							class="page-link"
							@click="goToPage(totalPages)"
							:disabled="page === totalPages"
							title="Last">
							&raquo;
						</button>
					</li>
				</ul>
			</nav>
			<span v-if="pageSizeStr !== 'ALL'"
				>Page {{ page }} of {{ totalPages }}</span
			>
			<span v-else>All users shown ({{ total }})</span>
		</div>
		<!-- Subscription Modal -->
		<div
			v-if="showModal"
			class="modal fade show"
			tabindex="-1"
			style="display: block; background: rgba(0, 0, 0, 0.5)"
			aria-modal="true"
			role="dialog">
			<div class="modal-dialog modal-dialog-centered">
				<div class="modal-content p-4">
					<h5>Subscription Info for {{ selectedUser?.email }}</h5>
					<div v-if="loadingSub">Loading...</div>
					<div v-else-if="subscription">
						<div>
							<b>Type:</b> {{ subscription.subscription_type || "basic" }}
						</div>
						<div><b>Start Date:</b> {{ subscription.start_date || "—" }}</div>
						<div><b>End Date:</b> {{ subscription.end_date || "—" }}</div>
						<div>
							<b>Platform:</b> {{ subscription.platform_active || "—" }}
						</div>
						<div>
							<b>Stripe Customer ID:</b>
							{{ subscription.stripe_customer_id || "—" }}
						</div>
					</div>
					<div v-else>No subscription found.</div>
					<div class="mt-3 d-flex gap-2">
						<button
							class="btn btn-success"
							@click="gift('giftYear')"
							v-if="!isPaying(subscription)">
							Gift Feedback (1 Year Plus)
						</button>
						<button
							class="btn btn-warning"
							@click="gift('giftForever')"
							v-if="!isPaying(subscription)">
							Free Forever
						</button>
						<button class="btn btn-secondary ms-auto" @click="closeModal">
							Close
						</button>
					</div>
					<div v-if="giftMsg" class="mt-2">{{ giftMsg }}</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
	import { ref, onMounted, watch, computed } from "vue"
	import { fetchWithAuth } from "@/utils/api-client"
	import { useUserStore } from "@/stores/user"

	const userStore = useUserStore()

	const users = ref([])
	const page = ref(1)
	const pageSize = ref(20)
	const total = ref(0)
	const totalPages = ref(1)
	const search = ref("")
	let searchTimeout = null
	const showModal = ref(false)
	const selectedUser = ref(null)
	const subscription = ref(null)
	const loadingSub = ref(false)
	const giftMsg = ref("")
	const pageSizeStr = ref("20")

	const fetchUsers = async () => {
		const params = new URLSearchParams({
			page: page.value,
			pageSize: pageSize.value,
			search: search.value.trim(),
		})
		const res = await fetchWithAuth(`/api/admin/users?${params}`)
		if (res.ok) {
			const data = await res.json()
			users.value = data.users
			total.value = data.total
			totalPages.value = data.totalPages
		}
	}

	const onSearch = () => {
		clearTimeout(searchTimeout)
		searchTimeout = setTimeout(() => {
			page.value = 1
			fetchUsers()
		}, 400)
	}

	const prevPage = () => {
		if (page.value > 1) {
			page.value--
			fetchUsers()
		}
	}
	const nextPage = () => {
		if (page.value < totalPages.value) {
			page.value++
			fetchUsers()
		}
	}

	const openSubscriptionModal = async (user) => {
		selectedUser.value = user
		showModal.value = true
		subscription.value = null
		loadingSub.value = true
		giftMsg.value = ""
		const res = await fetchWithAuth(`/api/admin/users?id=${user.id}`)
		if (res.ok) {
			subscription.value = await res.json()
		}
		loadingSub.value = false
	}
	const closeModal = () => {
		showModal.value = false
		selectedUser.value = null
		subscription.value = null
		giftMsg.value = ""
	}
	const gift = async (type) => {
		if (!selectedUser.value) return
		giftMsg.value = "Processing..."
		const res = await fetchWithAuth("/api/admin/users", {
			method: "PATCH",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				action: type,
				userId: selectedUser.value.id,
			}),
		})
		if (res.ok) {
			giftMsg.value =
				type === "giftYear" ? "Gifted 1 year of Plus!" : "Gifted Plus Forever!"
			// Refresh subscription info
			openSubscriptionModal(selectedUser.value)
		} else {
			giftMsg.value = "Failed to gift subscription."
		}
	}

	const changeRole = async (user, newRole) => {
		const res = await fetchWithAuth("/api/admin/users", {
			method: "PATCH",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				action: "setRole",
				userId: user.id,
				role: newRole,
			}),
		})
		if (res.ok) {
			user.role = newRole
		} else {
			alert("Failed to change role")
		}
	}

	const isPaying = (sub) => {
		return (
			sub?.platform_active === "android" ||
			sub?.platform_active === "ios" ||
			sub?.platform_active === "web"
		)
	}

	const onPageSizeChange = () => {
		// console.log("IN HERE")
		if (pageSizeStr.value === "ALL") {
			pageSize.value = total.value || 99999 // or some large number
		} else {
			pageSize.value = parseInt(pageSizeStr.value, 10)
		}
		page.value = 1
		fetchUsers()
	}

	const goToPage = (num) => {
		if (num >= 1 && num <= totalPages.value) {
			page.value = num
			fetchUsers()
		}
	}

	watch(page, fetchUsers)
	onMounted(fetchUsers)
</script>
